import * as React from "react";

import { Root, Thumb } from "@radix-ui/react-switch";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";

type SwitchProps = {
  isOn?: boolean;
  onChange?(isOn: boolean): void;
  trackClassName?: string;
  thumbClassName?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  tooltipText?: string | null;
  id?: string;
  size: "base" | "sm";
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
};
const Switch = ({
  isOn,
  onChange,
  trackClassName,
  thumbClassName,
  style,
  disabled,
  tooltipText,
  size,
  id,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  ...props
}: SwitchProps) => {
  const transition =
    size === "base" ? "translate-x-[20px]" : "translate-x-[16px]";

  return (
    <Tooltip content={tooltipText} triggerAsChild>
      <div tabIndex={disabled ? 0 : undefined}>
        <Root
          id={id}
          disabled={disabled ?? false}
          defaultChecked={isOn}
          onCheckedChange={onChange}
          className={twMerge(
            "relative flex shrink-0 cursor-pointer items-center rounded-full transition-colors",
            size === "base" && "h-[24px] w-[42px]",
            size === "sm" && "h-[18px] w-[32px]",
            isOn ? "bg-primary" : "bg-surface",
            disabled && "cursor-not-allowed opacity-50",
            trackClassName,
          )}
          style={{ ...style }}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
        >
          <Thumb asChild>
            <div
              {...props}
              className={twMerge(
                "shadow-xs block rounded-full bg-white transition-transform duration-200",
                size === "base" && "h-[20px] w-[20px]",
                size === "sm" && "h-[14px] w-[14px]",
                isOn ? transition : "translate-x-[2px]",
                thumbClassName,
              )}
            />
          </Thumb>
        </Root>
      </div>
    </Tooltip>
  );
};

export default Switch;
